import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { isLeaderPlus, isMemberPlus, isAdmin } from '../../auth/roleUtils';

interface RequireRoleProps {
  role: string;
  children: React.ReactNode;
}

export function RequireRole({ role, children }: RequireRoleProps) {
  const auth = useAuth();
  const navigate = useNavigate();
  const hasNavigated = useRef(false);

  useEffect(() => {
    // Only check auth after loading completes
    if (auth.loading) return;
    
    // Prevent multiple navigations
    if (hasNavigated.current) return;

    if (!auth.isAuthenticated) {
      hasNavigated.current = true;
      navigate('/login', { replace: true });
      return;
    }

    // Determine if user has required role
    let hasRequiredRole = false;

    if (role === 'BRG.Admin') {
      hasRequiredRole = isAdmin(auth.roles);
    } else if (role === 'BRG-Lead') {
      hasRequiredRole = isLeaderPlus(auth.roles);
    } else if (role === 'BRG.Member') {
      hasRequiredRole = isMemberPlus(auth.roles);
    } else {
      // Default role check
      hasRequiredRole = auth.roles.includes(role);
    }

    if (!hasRequiredRole) {
      // Users without Admin/Lead role are denied access and shown message on login page
      hasNavigated.current = true;
      navigate('/login?reason=access_denied', { replace: true });
    }
  }, [auth.isAuthenticated, auth.loading, auth.roles, role, navigate]);

  // Show loading state while auth is initializing
  if (auth.loading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh' 
      }}>
        Loading authentication...
      </div>
    );
  }

  // Show loading briefly while redirect is happening
  if (!auth.isAuthenticated) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh' 
      }}>
        Redirecting to login...
      </div>
    );
  }

  return <>{children}</>;
}
